import { SegmentGroup as ArkSegmentGroup } from "@ark-ui/react";
import type { ReactNode } from "react";
import { twJoin } from "tailwind-merge";

export type SegmentGroupItem<TValue extends string = string> = {
  label: string;
  value: TValue;
  "data-testid"?: string;
};

interface SegmentGroupProps<TValue extends string = string> {
  items: readonly SegmentGroupItem<TValue>[];
  value: TValue;
  onChange: (value: TValue) => void;
  width?: "full" | "content";
  ariaLabel?: string;
}

export function SegmentGroup<TValue extends string = string>({
  items,
  value,
  onChange,
  width = "full",
  ariaLabel,
}: SegmentGroupProps<TValue>): ReactNode {
  return (
    <ArkSegmentGroup.Root
      value={value}
      onValueChange={(details) => onChange((details.value as TValue) || items[0].value)}
      aria-label={ariaLabel}
      className={twJoin("flex rounded-lg bg-background-secondary p-space-xs", width === "full" ? "w-full" : "w-fit")}
    >
      {items.map((item) => (
        <ArkSegmentGroup.Item
          className={twJoin(
            "z-raised flex h-toggle-button-height-sm cursor-pointer items-center justify-center rounded-md bg-transparent px-toggle-button-inline-sm transition-colors data-focus-visible:ring-2 data-focus-visible:ring-border-focus data-focus-visible:ring-offset-2 data-[state='checked']:bg-background-default data-[state='checked']:shadow-raised",
            width === "full" ? "min-w-0 flex-1 overflow-hidden" : "shrink-0",
          )}
          data-testid={item["data-testid"]}
          key={item.value}
          value={item.value}
        >
          <ArkSegmentGroup.ItemText className="truncate text-caption-emphasis data-[state='unchecked']:text-text-secondary">
            {item.label}
          </ArkSegmentGroup.ItemText>
          <ArkSegmentGroup.ItemControl />
          <ArkSegmentGroup.ItemHiddenInput />
        </ArkSegmentGroup.Item>
      ))}
    </ArkSegmentGroup.Root>
  );
}
